/**
 * Утилиты для работы с российским номером телефона на фронте.
 *
 * Зачем отдельный файл: и в публичной регистрации
 * (`RegistrationLastStepPage.vue`), и в админской модалке «Создать
 * аккаунт» (`AdminUsersPage.vue`) нужна одинаковая логика нормализации и
 * валидации, чтобы бэк получал строку строго в формате `+7XXXXXXXXXX` и
 * не пришлось повторять regex.
 */

/**
 * Приводит произвольный пользовательский ввод к формату `+7XXXXXXXXXX`.
 *
 * Принимает всё, что человек мог набрать («+7 (999) 123-45-67», «8 999 …»,
 * «79991234567»). Возвращает нормализованную строку или `null`, если
 * восстановить валидный российский номер не удалось.
 *
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function normalizeRuPhone(raw) {
    if (raw == null) return null
    const digitsOnly = String(raw).replace(/\D+/g, '')
    if (!digitsOnly) return null

    let d = digitsOnly
    // Первый знак «8» в РФ трактуется как «+7» — приводим к единому виду.
    if (d.length === 11 && d.startsWith('8')) {
        d = '7' + d.slice(1)
    }
    // Пользователь мог ввести без кода страны — 10 цифр начинаются с 9…
    // (мобильные). Достраиваем +7.
    if (d.length === 10 && d.startsWith('9')) {
        d = '7' + d
    }

    if (d.length !== 11 || d[0] !== '7') return null
    return '+' + d
}

/**
 * true, если значение либо пустое (номер не обязателен), либо приводится
 * к валидному российскому формату. Используется в клиентской валидации
 * форм: не блокируем сабмит, если поле пустое.
 *
 * @param {string|null|undefined} raw
 * @returns {boolean}
 */
export function isValidRuPhoneOrEmpty(raw) {
    const trimmed = (raw ?? '').toString().trim()
    if (!trimmed) return true
    return normalizeRuPhone(trimmed) !== null
}

/**
 * Форматирует произвольный ввод под маску `+7 (XXX) XXX-XX-XX` по мере
 * набора. Работает с любыми вставками: «8...», «+7...», «79991234567»,
 * «+7 (999) 123 45 67» — всё сводится к единому виду.
 *
 * Правила:
 *   - Пустой ввод → пустая строка (чтобы placeholder не «залипал» плейсхолдером `+7 (`).
 *   - Первая «8» трактуется как «7» — визуально всегда «+7».
 *   - Ведущая «9…» без кода страны — считаем, что это сразу с кода города,
 *     подставляем `+7` (типичное поведение сайтов).
 *   - Всё, что после 11-й цифры (включая ведущую 7) — обрезается.
 *   - Разделители (пробелы/скобки/дефисы) в исходном тексте игнорируются;
 *     функция сама расставит их согласно маске.
 *
 * Функция симметрична к `normalizeRuPhone` и `isValidRuPhoneOrEmpty`:
 * форма отправляет на бэк нормализованное значение `+7XXXXXXXXXX`, а
 * пользователю показывает удобно отформатированное.
 *
 * @param {string|null|undefined} raw
 * @returns {string}
 */
export function formatRuPhoneInput(raw) {
    if (raw == null) return ''
    let digits = String(raw).replace(/\D+/g, '')
    if (!digits) return ''

    // «8XXXXXXXXXX» → «7XXXXXXXXXX». Делаем это только если длина уже 11 —
    // иначе первое напечатанное «8» превратится в «7» ещё до того, как
    // пользователь дописал номер, и он увидит «+7 (999)…» вместо «8».
    if (digits.length === 11 && digits[0] === '8') {
        digits = '7' + digits.slice(1)
    }

    // Если начали с «9…» и длина 10 — это код города без «7» впереди.
    // Дорисовываем ведущую «7», чтобы пользователь сразу увидел «+7 (9…».
    if (digits.length === 10 && digits[0] === '9') {
        digits = '7' + digits
    }

    // Если первая цифра не «7» — считаем, что пользователь только начал
    // печатать, и подставляем «7» слева. Это даёт мгновенный «+7» в поле
    // при первом же нажатии клавиши.
    if (digits[0] !== '7') {
        digits = '7' + digits
    }

    // Больше 11 цифр в российском номере (с ведущей 7) быть не может —
    // обрезаем, чтобы не сломать маску при paste'е длинного мусора.
    digits = digits.slice(0, 11)

    // digits[0] — всегда «7», следом до 10 цифр самого номера.
    const rest = digits.slice(1)
    const p1 = rest.slice(0, 3)
    const p2 = rest.slice(3, 6)
    const p3 = rest.slice(6, 8)
    const p4 = rest.slice(8, 10)

    let out = '+7'
    if (p1.length) {
        out += ' (' + p1
        if (p1.length === 3) out += ')'
    }
    if (p2.length) {
        out += ' ' + p2
    }
    if (p3.length) {
        out += '-' + p3
    }
    if (p4.length) {
        out += '-' + p4
    }
    return out
}

